<script setup lang="ts">
import { computed } from 'vue';

const { t } = useLocale();
const { locale: localeI18n } = useI18n();

type ProxyTypeModel = 'mobile' | 'corporate' | 'resident' | 'all';

const props = defineProps<{ title: string, type: ProxyTypeModel }>();

interface ItemModel {
  country: string,
  count: string,
  flag: string,
  text?: string,
  subtext?: string,
  default: boolean,
}

const items: Record<string, ItemModel[]> = {
  all: [
    { default: true, country: 'США', count: '489 233 IPs', flag: 'USA.png' },
    { default: true, country: 'Великобритания', count: '78 732 IPs', flag: 'UK.png' },
    { default: true, country: 'Россия', count: '397 824 IPs', flag: 'Russia.png' },
    { default: true, country: 'Германия', count: '86 724 IPs', flag: 'Germany.png' },
    { default: true, country: 'Франция', count: '42 190 IPs', flag: 'France.png' },
    { default: true, country: 'Бразилия', count: '95 604 IPs', flag: 'Brazil.png' },
    { default: true, country: 'Польша', count: '87 396 IPs', flag: 'Poland.png' },
    { default: true, country: 'Индия', count: '871 224 IPs', flag: 'India.png' },
    { default: true, country: 'Украина', count: '74 345 IPs', flag: 'Ukraine.png' },
    { default: false, country: '', count: '', flag: 'Earth.png', text: '+ 192 Других гео' },
  ],
  resident: [
    { default: true, country: 'США', count: '489 233 IPs', flag: 'USA.png' },
    { default: true, country: 'Германия', count: '86 724 IPs', flag: 'Germany.png' },
    { default: true, country: 'Великобритания', count: '78 732 IPs', flag: 'UK.png' },
    { default: true, country: 'Россия', count: '397 824 IPs', flag: 'Russia.png' },
    { default: true, country: 'Бразилия', count: '95 604 IPs', flag: 'Brazil.png' },
    { default: true, country: 'Польша', count: '87 396 IPs', flag: 'Poland.png' },
    { default: true, country: 'Италия', count: '62 095 IPs', flag: 'Italy.png' },
    { default: false, country: '', count: '', flag: 'Earth.png', text: '+ 192 Других гео', subtext: '> Посмотреть все местоположения' },
  ],
  mobile: [
    { default: true, country: 'США', count: '357 250 IPs', flag: 'USA.png' },
    { default: true, country: 'Польша', count: '72 710 IPs', flag: 'Poland.png' },
    { default: true, country: 'Россия', count: '492 747 IPs', flag: 'Russia.png' },
    { default: true, country: 'Германия', count: '54 570 IPs', flag: 'Germany.png' },
    { default: true, country: 'Бразилия', count: '64 845 IPs', flag: 'Brazil.png' },
    { default: true, country: 'Испания', count: '26 350 IPs', flag: 'Spain.png' },
    { default: true, country: 'Великобритания', count: '27 727 IPs', flag: 'UK.png' },
    { default: true, country: 'Франция', count: '42 190 IPs', flag: 'France.png' },
  ],
  corporate: [
    { default: true, country: 'США', count: '4 270 IPs', flag: 'USA.png' },
    { default: true, country: 'Великобритания', count: '13 374 IPs', flag: 'UK.png' },
    { default: true, country: 'Россия', count: '28 233 IPs', flag: 'Russia.png' },
    { default: true, country: 'Канада', count: '10 760 IPs', flag: 'Canada.png' },
    { default: true, country: 'Германия', count: '14 436 IPs', flag: 'Germany.png' },
    { default: true, country: 'Франция', count: '33 687 IPs', flag: 'France.png' },
    { default: true, country: 'Нидерланды', count: '18 810 IPs', flag: 'Netherlands.png' },
    { default: true, country: 'Китай', count: '3 150 IPs', flag: 'China.png' },
  ],
};

const itemsByType = computed(() => {
  return items[props.type];
});
</script>

<template>
  <div class="proxy-geo">
    <h2 class="sx-landing-title roxy-geo__title">
      {{ t(title) }}
    </h2>

    <div class="proxy-geo__content" :class="type">
      <div v-for="(item, index) in itemsByType" :key="index" class="proxy-geo__item">
        <div class="proxy-geo__flag">
          <img :src="`/flags/${item.flag}`" alt="flag" loading="lazy" />
        </div>
        <div v-if="item.default" class="proxy-geo__info-wrapper">
          <div class="proxy-geo__text">
            {{ t(item.country) }}
          </div>
          <div class="proxy-geo__text">
            {{ item.count }}
          </div>
        </div>
        <div v-else class="proxy-geo__info-wrapper">
          <div class="proxy-geo__text">
            {{ t(item.text!) }}
          </div>
        </div>
      </div>
    </div>

    <NuxtLink v-if="type === 'all'" :to="`/${localeI18n}/geo`" class="sx--btn proxy-geo__btn--transparent">
      {{ t('Все локации') }}
    </NuxtLink>
  </div>
</template>

<style lang="scss" scoped>
.proxy-geo {
  font-family: AlsHauss, sans-serif;
  display: flex;
  flex-direction: column;
  gap: 72px;
  align-items: center;
  justify-content: center;
  height: 772px;

  &__btn {
    &--transparent {
      outline: 1px solid #000000;
      background: transparent;
      color: #000;
      padding: 12px 20px;
      height: 44px;
      font-size: 16px;
      font-weight: 500;
      line-height: 20px;
      min-width: 136px;

      &:hover {
        color: #000;
        background: #eeeeee;
      }

      &:active {
        background: #F7F7F7;
      }
    }
  }

  &__title {
    text-align: center;
  }

  @media (width < 1040px) {
    & {
      height: auto !important;
    }
  }

  &__content {
    display: grid;
    row-gap: 44px;
    column-gap: 100px;
    grid-template-columns: repeat(4, 225px);
    grid-template-rows: 45px;
    max-width: 1200px;
    margin: 0 auto;
    @media (max-width: 600px) {
      row-gap: 1rem !important;
    }

    &.all {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      justify-content: center;
      column-gap: 98px;
    }

    @media (width < 1380px) {
      grid-template-columns: repeat(3, 225px);
    }
    @media (width < 1040px) {
      grid-template-columns: repeat(2, 225px);
    }
    @media (width < 640px) {
      grid-template-columns: repeat(1, 225px);
    }
  }

  &__item {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 16px;
  }

  &__flag {
    min-width: 50px;

    img {
      width: 44px;
      height: 44px;
    }
  }

  &__text {
    width: 160px;
    font-size: 16px;
    font-weight: 400;
    line-height: 24px;
  }
}
</style>
